<script setup lang="ts">
import { useBackToClose } from '@/composables/useBackToClose'
import { localizePlace } from '@/data/regions'
import { ref, computed, watch, nextTick, onUnmounted } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from '@/composables/useI18n'
import {
  Sparkles,
  X,
  Send,
  Bot,
  ArrowUpRight,
  Loader2,
  RotateCcw,
  ImageOff
} from 'lucide-vue-next'
import { useFeatureStore } from '@/stores/feature'

interface Props {
  modelValue: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const { t, locale, currentLang } = useI18n()
const featureStore = useFeatureStore()

const promptInput = ref('')
const inputEl = ref<HTMLInputElement | null>(null)
const messagesContainer = ref<HTMLDivElement | null>(null)
const isLoading = ref(false)

// A category id in the reader's language ('real-estate' -> categories.realEstate);
// nothing rather than a raw id when there is no translation.
function categoryLabel(id: string): string {
  if (!id) return ''
  return t(`categories.${String(id).replace(/[-_](\w)/g, (_m, c: string) => c.toUpperCase())}`)
}

// Models write Markdown now and then even when asked not to. The bubble
// shows plain text, so **bold** becomes bold and heading marks are dropped —
// as text segments, never as HTML.
function replySegments(text: string): Array<{ text: string; bold: boolean }> {
  const clean = String(text || '').replace(/^#{1,6}\s+/gm, '').replace(/^\s*[*]\s+/gm, '– ')
  return clean.split(/\*\*(.+?)\*\*/g).map((part, i) => ({ text: part, bold: i % 2 === 1 })).filter(p => p.text)
}

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  // Nothing matched: the most viewed live lots, under their own heading.
  popularAuctions?: ChatMessage['recommendedAuctions']
  recommendedAuctions?: Array<{
    id: string
    title: string
    currentPrice: number
    category: string
    city: string
    imageUrl: string
    price?: {
      amount: string
      currency: string
      formatted: string
    }
  }>
}

const messages = ref<ChatMessage[]>([
  {
    role: 'assistant',
    content: t('modules.aiAssistant.welcomeMessage')
  }
])

// Auto-update initial message if user changes language before chatting
watch(locale, () => {
  if (messages.value.length === 1 && messages.value[0].role === 'assistant') {
    messages.value[0].content = t('modules.aiAssistant.welcomeMessage')
  }
})

const quickChips = computed(() => [
  t('modules.aiAssistant.chip1'),
  t('modules.aiAssistant.chip2'),
  t('modules.aiAssistant.chip3'),
  t('modules.aiAssistant.chip4')
])

// --- Dialog keyboard behaviour -------------------------------------------
// Esc has to close the sheet from anywhere inside it (not only while the
// composer holds focus), and Tab must stay within the overlay while it is open.
const panelRef = ref<HTMLElement | null>(null)

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function focusableNodes(): HTMLElement[] {
  if (!panelRef.value) return []
  return Array.from(panelRef.value.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    .filter(el => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement)
}

function onDialogKeydown(event: KeyboardEvent) {
  if (!props.modelValue) return

  if (event.key === 'Escape') {
    event.preventDefault()
    close()
    return
  }

  if (event.key !== 'Tab') return
  const nodes = focusableNodes()
  if (!nodes.length) return

  const first = nodes[0]
  const last = nodes[nodes.length - 1]
  const active = document.activeElement as HTMLElement | null
  const inside = !!active && !!panelRef.value?.contains(active)

  if (!inside) {
    event.preventDefault()
    ;(event.shiftKey ? last : first).focus()
  } else if (event.shiftKey && active === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

watch(() => props.modelValue, (open) => {
  if (open) {
    nextTick(() => {
      inputEl.value?.focus()
      scrollToBottom()
    })
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onDialogKeydown)
  } else {
    document.body.style.overflow = ''
    document.removeEventListener('keydown', onDialogKeydown)
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', onDialogKeydown)
})

function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}

function close() {
  emit('update:modelValue', false)
}

// Phone back button closes this overlay instead of leaving the page.
useBackToClose(() => props.modelValue, () => close())

function resetChat() {
  messages.value = [
    {
      role: 'assistant',
      content: t('modules.aiAssistant.chatReset')
    }
  ]
}

async function sendQuery(text?: string) {
  const query = (text || promptInput.value).trim()
  if (!query || isLoading.value) return

  promptInput.value = ''
  messages.value.push({ role: 'user', content: query })
  scrollToBottom()

  isLoading.value = true
  try {
    const history = messages.value.slice(-6).map(m => ({ role: m.role, content: m.content }))
    const res = await featureStore.askAiAssistant(query, history, locale.value)

    messages.value.push({
      role: 'assistant',
      content: res.reply,
      recommendedAuctions: res.recommendedAuctions || (res as any).products || [],
      popularAuctions: (res as any).matched === false ? (res as any).popularAuctions || [] : [],
    })
  } catch (err) {
    // Fallback response with localized message
    messages.value.push({
      role: 'assistant',
      content: t('modules.aiAssistant.fallbackError'),
      recommendedAuctions: []
    })
  } finally {
    isLoading.value = false
    scrollToBottom()
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="modelValue"
        class="fixed inset-0 z-50 flex items-end justify-center bg-ink/60 backdrop-blur-sm sm:items-center sm:p-4"
        @click.self="close"
      >
        <!-- Panel: bottom sheet on mobile, centred card on desktop -->
        <div
          ref="panelRef"
          class="flex h-[640px] max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-[1.5rem] bg-surface shadow-[0_24px_60px_-24px_rgba(20,23,31,0.6)] animate-slide-up sm:rounded-[1.5rem] sm:animate-scale-in"
          role="dialog"
          aria-modal="true"
          :aria-label="t('modules.aiAssistant.title')"
        >
          <!-- Header -->
          <div class="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-3.5 sm:px-5">
            <div class="flex min-w-0 items-center gap-3">
              <span class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/20 text-onPrimaryContainer">
                <Bot class="h-5 w-5" />
              </span>
              <div class="min-w-0">
                <div class="flex items-center gap-1.5">
                  <h3 class="truncate font-display text-[15px] font-semibold text-text-primary">{{ t('modules.aiAssistant.title') }}</h3>
                  <span class="shrink-0 rounded-full bg-primary/20 px-2 py-0.5 text-[11px] font-bold uppercase text-onPrimaryContainer">{{ t('modules.aiAssistant.copilotBadge') }}</span>
                </div>
                <p class="truncate text-[11px] text-text-muted">{{ t('modules.aiAssistant.subtitle') }}</p>
              </div>
            </div>

            <div class="flex shrink-0 items-center gap-1">
              <button
                type="button"
                class="rounded-lg p-2 text-text-secondary transition hover:bg-accent hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                :title="t('modules.aiAssistant.clearChat')"
                :aria-label="t('modules.aiAssistant.clearChat')"
                @click="resetChat"
              >
                <RotateCcw class="h-4 w-4" />
              </button>
              <button
                type="button"
                class="rounded-lg p-2 text-text-secondary transition hover:bg-accent hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                :title="t('common.close')"
                :aria-label="t('common.close')"
                @click="close"
              >
                <X class="h-4 w-4" />
              </button>
            </div>
          </div>

          <!-- Messages -->
          <div
            ref="messagesContainer"
            class="flex-1 space-y-4 overflow-y-auto overscroll-contain bg-background p-4 sm:p-5"
          >
            <div
              v-for="(msg, i) in messages"
              :key="i"
              class="flex flex-col gap-2"
              :class="msg.role === 'user' ? 'items-end' : 'items-start'"
            >
              <div class="flex max-w-[88%] items-start gap-2.5">
                <!-- Assistant avatar -->
                <span
                  v-if="msg.role === 'assistant'"
                  class="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary text-onPrimary"
                >
                  <Sparkles class="h-3.5 w-3.5" />
                </span>

                <!-- Bubble -->
                <div
                  class="whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed"
                  :class="msg.role === 'user'
                    ? 'rounded-tr-md bg-primary/15 text-text-primary'
                    : 'rounded-tl-md bg-accent text-text-primary'"
                >
                  <template v-if="msg.role === 'assistant'"><template v-for="(seg, i) in replySegments(msg.content)" :key="i"><strong v-if="seg.bold" class="font-semibold">{{ seg.text }}</strong><template v-else>{{ seg.text }}</template></template></template>
                  <template v-else>{{ msg.content }}</template>
                </div>
              </div>

              <!-- Recommended lots -->
              <div
                v-if="msg.recommendedAuctions && msg.recommendedAuctions.length > 0"
                class="grid w-full grid-cols-1 gap-2 pl-9 pr-2 sm:grid-cols-2"
              >
                <RouterLink
                  v-for="lot in msg.recommendedAuctions"
                  :key="lot.id"
                  :to="'/auctions/' + lot.id"
                  class="group flex items-center gap-3 rounded-card border border-border bg-surface p-2.5 transition hover:-translate-y-0.5 hover:border-ink/15 hover:shadow-[0_16px_36px_-16px_rgba(20,23,31,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  @click="close"
                >
                  <img
                    v-if="lot.imageUrl"
                    :src="lot.imageUrl"
                    :alt="lot.title"
                    class="h-12 w-12 shrink-0 rounded-lg bg-accent object-cover"
                    loading="lazy"
                  />
                  <span v-else class="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-accent text-text-muted">
                    <ImageOff class="h-4 w-4" />
                  </span>
                  <div class="min-w-0 flex-1">
                    <h4 class="truncate text-xs font-semibold text-text-primary">
                      {{ lot.title }}
                    </h4>
                    <span class="mt-0.5 block font-display text-[13px] font-bold tabular text-text-primary">
                      {{ lot.price?.formatted || (lot.currentPrice?.toLocaleString() + ' ' + t('common.currency')) }}
                    </span>
                    <span class="block truncate text-[11px] text-text-muted">
                      {{ [localizePlace(lot.city, currentLang), categoryLabel(lot.category)].filter(Boolean).join(' · ') }}
                    </span>
                  </div>
                  <ArrowUpRight class="h-4 w-4 shrink-0 text-text-muted transition group-hover:text-primary-hover" />
                </RouterLink>
              </div>
              <p v-if="msg.popularAuctions && msg.popularAuctions.length > 0" class="pl-9 pr-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-text-muted">{{ t('modules.aiAssistant.popularHeading') }}</p>
              <!-- Nothing matched: popular lots, labelled as such -->
              <div
                v-if="msg.popularAuctions && msg.popularAuctions.length > 0"
                class="grid w-full grid-cols-1 gap-2 pl-9 pr-2 sm:grid-cols-2"
              >
                <RouterLink
                  v-for="lot in msg.popularAuctions"
                  :key="lot.id"
                  :to="'/auctions/' + lot.id"
                  class="group flex items-center gap-3 rounded-card border border-border bg-surface p-2.5 transition hover:-translate-y-0.5 hover:border-ink/15 hover:shadow-[0_16px_36px_-16px_rgba(20,23,31,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  @click="close"
                >
                  <img
                    v-if="lot.imageUrl"
                    :src="lot.imageUrl"
                    :alt="lot.title"
                    class="h-12 w-12 shrink-0 rounded-lg bg-accent object-cover"
                    loading="lazy"
                  />
                  <span v-else class="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-accent text-text-muted">
                    <ImageOff class="h-4 w-4" />
                  </span>
                  <div class="min-w-0 flex-1">
                    <h4 class="truncate text-xs font-semibold text-text-primary">
                      {{ lot.title }}
                    </h4>
                    <span class="mt-0.5 block font-display text-[13px] font-bold tabular text-text-primary">
                      {{ lot.price?.formatted || (lot.currentPrice?.toLocaleString() + ' ' + t('common.currency')) }}
                    </span>
                    <span class="block truncate text-[11px] text-text-muted">
                      {{ [localizePlace(lot.city, currentLang), categoryLabel(lot.category)].filter(Boolean).join(' · ') }}
                    </span>
                  </div>
                  <ArrowUpRight class="h-4 w-4 shrink-0 text-text-muted transition group-hover:text-primary-hover" />
                </RouterLink>
              </div>
            </div>

            <!-- Thinking indicator -->
            <div v-if="isLoading" class="flex items-center gap-2 pl-9 text-xs text-text-muted">
              <Loader2 class="h-3.5 w-3.5 animate-spin text-primary-hover" />
              <span>{{ t('modules.aiAssistant.thinking') }}</span>
            </div>
          </div>

          <!-- Suggestion chips (shown if enabled in admin) -->
          <div
            v-if="featureStore.config.aiAssistant?.showSuggestions"
            class="no-scrollbar flex shrink-0 items-center gap-1.5 overflow-x-auto border-t border-border bg-surface px-4 py-2"
          >
            <button
              v-for="chip in quickChips"
              :key="chip"
              type="button"
              class="shrink-0 whitespace-nowrap rounded-full bg-accent px-3 py-1.5 text-[11px] font-semibold text-text-secondary transition hover:bg-primary/20 hover:text-onPrimaryContainer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              @click="sendQuery(chip)"
            >
              {{ chip }}
            </button>
          </div>

          <!-- Composer -->
          <div class="shrink-0 border-t border-border bg-surface p-3 sm:p-4">
            <div class="relative flex items-center">
              <input
                ref="inputEl"
                v-model="promptInput"
                type="text"
                :placeholder="t('modules.aiAssistant.inputPlaceholder')"
                class="w-full rounded-xl border border-border bg-surface py-2.5 pl-3.5 pr-12 text-sm text-text-primary placeholder:text-text-muted transition focus:border-ink focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60"
                :disabled="isLoading"
                @keydown.enter="sendQuery()"
              />
              <button
                type="button"
                :disabled="!promptInput.trim() || isLoading"
                :title="t('modules.aiAssistant.send')"
                :aria-label="t('modules.aiAssistant.send')"
                class="absolute right-1.5 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-onPrimary transition hover:bg-primary-hover active:scale-[0.98] disabled:opacity-40 disabled:hover:bg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/40"
                @click="sendQuery()"
              >
                <Send class="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes slideUp {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}
.animate-slide-up {
  animation: slideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes scaleIn {
  from { transform: scale(0.97); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
@media (min-width: 640px) {
  .sm\:animate-scale-in {
    animation: scaleIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  }
}
</style>
